// Gates the whole app behind Entra ID sign-in + a security-group check.
//
//  1. Not signed in → our own branded sign-in page (see SignIn.jsx). The user
//     has to press "Sign in with Microsoft" to be redirected; we deliberately do
//     NOT redirect on arrival, so a first-time visitor sees where they are
//     before being handed to login.microsoftonline.com.
//  2. Signed in → GroupGate checks the account is a member of the required
//     security group and shows an "access denied" screen otherwise.
//
// When AUTH_ENABLED is false (local dev), the gate is bypassed entirely.
import { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { loginRequest, REQUIRED_GROUP_ID, AUTH_ENABLED } from "./authConfig";
import SignIn, { AuthCard } from "./SignIn";

function Loading() {
  return (
    <AuthCard>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
        <CircularProgress size={22} />
        <Typography color="text.secondary">Signing you in…</Typography>
      </Box>
    </AuthCard>
  );
}

// Group object ids from the ID token's groups claim (lower-cased; Entra is
// inconsistent about GUID casing).
function groupsOf(account) {
  const claims = account?.idTokenClaims || {};
  const groups = Array.isArray(claims.groups) ? claims.groups : [];
  return groups.map((g) => String(g).trim().toLowerCase());
}

// True when Entra replaced the groups claim with a pointer to Graph because the
// account is in too many groups to fit in the token. We can't resolve that in the
// browser, so we let the request through and let the backend have the last word
// rather than locking someone out on missing information.
function groupsOverflowed(account) {
  const claims = account?.idTokenClaims || {};
  return Boolean(claims.hasgroups || claims._claim_names?.groups);
}

function GroupGate({ account, children }) {
  const { instance } = useMsal();

  const allowed =
    !REQUIRED_GROUP_ID ||
    groupsOverflowed(account) ||
    groupsOf(account).includes(REQUIRED_GROUP_ID);

  if (!allowed) {
    return (
      <AuthCard>
        <Typography variant="h6">Access denied</Typography>
        <Typography color="text.secondary">
          You are signed in as <strong>{account?.username}</strong>, but your account
          has not been granted access to Shelter Stone. Please ask an administrator
          to add you to the Shelter Stone users group.
        </Typography>
        <Button variant="outlined" onClick={() => instance.logoutRedirect()} sx={{ mt: 1 }}>
          Sign out
        </Button>
      </AuthCard>
    );
  }
  return children;
}

function Gate({ children, initialError }) {
  const { instance, accounts, inProgress } = useMsal();
  // An error from the redirect we came back on (captured in index.js, since that
  // is what awaits handleRedirectPromise) or from starting a new sign-in.
  const [error, setError] = useState(initialError || null);
  const [busy, setBusy] = useState(false);
  const account = instance.getActiveAccount() || accounts[0];

  if (account) return <GroupGate account={account}>{children}</GroupGate>;

  // MSAL is still starting up or mid-interaction (typically completing the
  // redirect back from Microsoft) — hold the spinner rather than flashing the
  // sign-in page at someone who is already on their way in.
  if (inProgress !== InteractionStatus.None) return <Loading />;

  const signIn = () => {
    setError(null);
    setBusy(true);
    // Navigates away on success, so `busy` is only cleared on failure.
    instance.loginRedirect(loginRequest).catch((e) => {
      setError(e);
      setBusy(false);
    });
  };

  return <SignIn onSignIn={signIn} busy={busy} error={error} />;
}

export default function AuthGate({ children, initialError }) {
  if (!AUTH_ENABLED) return children;
  return <Gate initialError={initialError}>{children}</Gate>;
}
